import { api, types } from "@/lib/openapi";
import { defineStore } from "pinia";
import { useToast } from "primevue/usetoast";
import { Ref, ref, watch } from "vue";
import { EntityType, useCurrentEntityManager } from "./entity-store";

export type BusinessEntityFieldDefinition = types.BusinessEntityFieldDefinition;
export type BusinessEntityFieldType = types.BusinessEntityFieldType;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const BusinessEntityFieldType = types.BusinessEntityFieldType;
export type BusinessEntity = types.BusinessEntity;
export type FieldType = types.BusinessEntityFieldDefinition & { id: number };

// 'Manage' entities within a domain here
// Uses generated openapi client
// Uses wrapper to automatically init the store on first use or when enties are not set
export const useBusinessEntitiesStore = () => {
    const _store = defineStore("entities", () => {
        const toast = useToast();

        const entities: Ref<BusinessEntity[]> = ref([]);

        // Indicate busy state (request in transit)
        const isLoading: Ref<boolean> = ref(false);

        // Contain http response statusCode, only set when not 200
        const statusCode: Ref<number | undefined> = ref(undefined);
        const _error = ref();

        const { domain, product } = useCurrentEntityManager();

        const controller = ref(new AbortController());

        const getEntities = async () => {
            // Abort any previously running requests and create a new controller
            controller.value.abort();
            controller.value = new AbortController();
            if (!domain.value?.identity) {
                entities.value = [];
                return;
            }

            isLoading.value = true;

            try {
                const { data, response } =
                    await api.startDomainBusinessEntityMgtGetBusinessEntitiesByDomainStartPost(
                        {
                            body: {
                                domain_id: domain.value.identity,
                            },
                            signal: controller.value.signal,
                        }
                    );

                if (response.status === 200) {
                    statusCode.value = 200;
                    if (data) {
                        entities.value = data;
                    }
                } else {
                    //TODO talk about error handling
                    // Alternatively we could just try and catch the HttpError and expose it as a prop
                    statusCode.value = response.status;
                    _error.value = data;
                }
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isLoading.value = false;
            }
        };

        watch(
            () => domain.value?.identity,
            async () => {
                await getEntities();
            },
            { immediate: true }
        );

        watch(_error, () => {
            if (_error.value)
                toast.add({
                    severity: "error",
                    summary: "Error " + statusCode.value,
                    detail: _error.value.detail
                        ? _error.value.detail[0].msg
                        : _error.value,
                });
            _error.value = undefined;
        });

        const updateEntity = async (entity: BusinessEntity) => {
            if (!entity) {
                return;
            }

            isLoading.value = true;

            const { response } =
                await api.startDomainBusinessEntityMgtUpdateBusinessEntityStartPost(
                    {
                        body: {
                            business_entity_id: entity.identity,
                            domain_id: entity.domain_id,
                            definition: entity.definition || [],
                            metadata: entity.metadata,
                        },
                    }
                );
            statusCode.value = response.status;
            if (response.ok) {
                await getEntities();
            } else {
                _error.value = response.text();
            }
            isLoading.value = false;
        };

        const createEntity = async (
            name: string,
            description?: string,
            definition: FieldType[] = []
        ) => {
            // name is required
            if (!name) {
                return;
            }
            isLoading.value = true;
            const { response } =
                await api.startDomainBusinessEntityMgtCreateBusinessEntityStartPost(
                    {
                        body: {
                            domain_id: domain.value?.identity || "",
                            definition: definition,
                            metadata: {
                                name,
                                description,
                            },
                        },
                    }
                );
            statusCode.value = response.status;
            if (response.ok) {
                await getEntities();
            } else {
                _error.value = response.text();
            }
            isLoading.value = false;
        };

        const deleteEntity = async (entityId: string) => {
            if (!entityId) {
                return;
            }

            isLoading.value = true;
            // we use optimistic mode here
            // https://github.com/oazapfts/oazapfts/tree/main?tab=readme-ov-file#optimistic-mode
            const { response } =
                await api.startDomainBusinessEntityMgtDeleteBusinessEntityStartPost(
                    {
                        body: {
                            domain_id: domain.value?.identity || "",
                            business_entity_id: entityId,
                        },
                    }
                );
            statusCode.value = response.status;
            if (response.ok) {
                await getEntities();
            } else {
                _error.value = response.text();
            }
            isLoading.value = false;
        };

        /*
         * generate children of entity
         */
        const generateChildren = async () => {
            const entityType = product.value?.identity
                ? EntityType.PRODUCT
                : EntityType.DOMAIN;
            const entityId =
                entityType === EntityType.DOMAIN
                    ? domain.value?.identity
                    : product.value?.identity;

            if (!entityId) {
                return [];
            }

            try {
                const { data } =
                    await api.startBizEntitiesEntityTypeEntityIdGenerateChildrenPost(
                        {
                            path: {
                                entity_id: entityId,
                                entity_type: entityType,
                            },
                        }
                    );

                if (data) {
                    return data.map(
                        (item: { name: string; description: string }) => ({
                            name: item.name,
                            description: item.description,
                        })
                    );
                }
                return [];
            } catch (error) {
                console.error("Error generating children:", error);
                throw error;
            }
        };

        return {
            entities,
            isLoading,
            statusCode,
            getEntities,
            updateEntity,
            createEntity,
            deleteEntity,
            generateChildren,
        };
    });

    const store = _store();
    if (!store.entities) {
        store.getEntities();
    }
    return store;
};
